export interface DashboardStatsDto {
  totalUsers: number;
  totalLicenses: number;
  totalCompanies: number;
  activeInstances: number;
  expiredLicenses: number;
  activeLicenses: number;
  seatsMax: number;
  blockedUsers: number;
  invitedUsers: number;
  expiring60d: number;
  unresolvedTickets: number;
  newTickets: number;
  openErrorReports: number;
}

export type AdminHealthStatus = 'online' | 'degraded' | 'offline';

export interface AdminHealthDto {
  licenseServer: AdminHealthStatus;
}

export type AdminLicenseStatus = 'active' | 'suspended' | 'expired';

export interface AdminMachineDto {
  id: number;
  host: string;
  userEmail: string;
  activatedAt: string;
  isOnline: boolean;
}

export interface AdminLicenseDto {
  id: number;
  activationKey: string;
  allowedInstances: number;
  allowedRegistrations: number;
  remainingExportElements: number | null;
  activationTime: string | null;
  dueTime: string | null;
  type: number;
  isActivated: boolean;
  companyName: string;
  registeredUsersCount: number;
  companyId: number | null;
  status: AdminLicenseStatus;
  notes: string;
  machinesCount: number;
}

export interface AdminLicenseDetailDto extends AdminLicenseDto {
  machines: AdminMachineDto[];
}

export type AdminUserRole = 'Super' | 'Admin' | 'Manager' | 'User';
export type AdminTenantUserRole = Exclude<AdminUserRole, 'Super'>;
export type AdminUserStatus = 'active' | 'invited' | 'blocked';

export interface AdminUserDto {
  id: number;
  username: string;
  email: string | null;
  companyId: number | null;
  companyName: string;
  role: AdminUserRole;
  status: AdminUserStatus;
  lastActiveAt: string | null;
  registrationTime: string;
  licenseIds: number[];
}

export interface AdminUserCreateRequest {
  username: string;
  email: string;
  companyId: number;
  role: AdminTenantUserRole;
  licenseIds: number[];
}

export interface AdminUserPatchRequest {
  username?: string;
  email?: string | null;
  companyId?: number;
  role?: AdminUserRole;
  status?: AdminUserStatus;
  licenseIds?: number[];
}

export type AdminCompanyPlan = 'Enterprise' | 'Team' | 'Trial';
export type AdminCompanyStatus = 'active' | 'trial' | 'suspended';

export interface AdminCompanyDto {
  id: number;
  name: string;
  usersCount: number;
  licensesCount: number;
  licenseKeys: string[];
  domain: string;
  plan: AdminCompanyPlan;
  country: string;
  contactEmail: string;
  status: AdminCompanyStatus;
  createdAt: string;
  notes: string;
}

export interface AdminCompanyUserSummary {
  id: number;
  username: string;
  email: string | null;
  role: AdminUserRole;
  status: AdminUserStatus;
}

export interface AdminCompanyLicenseSummary {
  id: number;
  activationKey: string;
  type: number;
  status: AdminLicenseStatus;
  registeredUsersCount: number;
  allowedRegistrations: number;
  remainingExportElements: number | null;
}

export interface AdminCompanyDetailDto extends AdminCompanyDto {
  users: AdminCompanyUserSummary[];
  licenses: AdminCompanyLicenseSummary[];
  deletePreview: AdminCompanyDeletePreview;
}

export interface AdminCompanyMutationRequest {
  name: string;
  domain: string;
  plan: AdminCompanyPlan;
  country: string;
  contactEmail: string;
  status: AdminCompanyStatus;
  notes: string;
}

export type AdminCompanyCreateRequest = AdminCompanyMutationRequest;
export type AdminCompanyPatchRequest = Partial<AdminCompanyMutationRequest>;

export interface AdminCompanyDeletePreview {
  users: number;
  licenses: number;
}

export interface AdminCompanyDeleteOptions {
  confirmCascade: boolean;
  expectedCounts?: AdminCompanyDeletePreview;
}

export type AdminCompanyBulkAction = 'suspend' | 'delete';

export type AdminTicketCategory = 'Bug' | 'Question' | 'Billing' | 'ErrorReport';
export type AdminTicketPriority = 'high' | 'medium' | 'low';
export type AdminTicketStatus = 'new' | 'open' | 'resolved';
export type AdminTicketMessageKind = 'user' | 'admin' | 'note';

export interface AdminTicketMessageDto {
  id: number;
  kind: AdminTicketMessageKind;
  authorName: string;
  createdAt: string;
  body: string;
}

export interface AdminTicketDto {
  id: number;
  number: string;
  subject: string;
  userId: number;
  userName: string;
  companyId: number | null;
  companyName: string;
  category: AdminTicketCategory;
  priority: AdminTicketPriority;
  status: AdminTicketStatus;
  assigneeId: number | null;
  assigneeName: string | null;
  pcfRunTraceId: string | null;
  createdAt: string;
  updatedAt: string;
  lastMessagePreview: string;
  messages?: AdminTicketMessageDto[];
}

export type AdminTicketListItemDto = Omit<AdminTicketDto, 'messages'>;

export interface AdminTicketStatusCountsDto {
  new: number;
  open: number;
  resolved: number;
}

export interface AdminTicketCategoryCountsDto {
  bug: number;
  question: number;
  billing: number;
  errorReport: number;
}

export interface AdminTicketListResponse extends AdminPageDto<AdminTicketListItemDto> {
  statusCounts: AdminTicketStatusCountsDto;
  categoryCounts: AdminTicketCategoryCountsDto;
}

export interface AdminTicketListQuery {
  status?: AdminTicketStatus | null;
  category?: AdminTicketCategory | null;
  q?: string;
  cursor?: string | null;
  limit?: number;
}

export interface AdminTicketPatchRequest {
  status?: AdminTicketStatus;
  priority?: AdminTicketPriority;
  assigneeId?: number | null;
  expectedUpdatedAt?: string;
}

export interface AdminTicketMessageRequest {
  kind: Exclude<AdminTicketMessageKind, 'user'>;
  body: string;
  expectedUpdatedAt?: string;
}

export interface AdminPageDto<T> {
  items: T[];
  total?: number;
  nextCursor?: string | null;
}

export interface AdminLicenseCreateRequest {
  companyId: number;
  type: number;
  allowedInstances: number;
  allowedRegistrations: number;
  remainingExportElements: number | null;
  dueTime: string | null;
  status: AdminLicenseStatus;
  notes: string;
}

export interface AdminLicensePatchRequest {
  companyId?: number;
  type?: number;
  allowedInstances?: number;
  allowedRegistrations?: number;
  remainingExportElements?: number | null;
  dueTime?: string | null;
  status?: AdminLicenseStatus;
  notes?: string;
}

export type AdminLicenseBulkAction = 'extend' | 'suspend' | 'delete';

export interface AdminLicenseBulkRequest {
  action: AdminLicenseBulkAction;
  ids: number[];
  params?: { months: number };
}

export interface AdminBulkFailure {
  id: number;
  code: string;
  message?: string;
}

export interface AdminBulkResponse {
  succeeded: number[];
  failed: AdminBulkFailure[];
}

export interface AdminApiError {
  code: string;
  message: string;
}

export interface AdminMutationOutcome {
  succeeded: true;
  refreshError: AdminApiError | null;
}

export interface AdminLicenseBulkOutcome extends AdminBulkResponse {
  refreshError: AdminApiError | null;
}

export type AdminUserBulkAction = 'block' | 'unblock' | 'delete';

export interface AdminUserBulkOutcome extends AdminBulkResponse {
  refreshError: AdminApiError | null;
}

export interface AdminCompanyBulkOutcome extends AdminBulkResponse {
  refreshError: AdminApiError | null;
}

export type AdminCompanyBulkDeletePreviews = Record<number, AdminCompanyDeletePreview>;

export interface AdminLicenseSaveResult {
  canonical: AdminLicenseDetailDto;
  patchSucceeded: boolean;
  patchError: AdminApiError | null;
  revokedMachineIds: number[];
  revokeFailures: AdminBulkFailure[];
  refreshError: AdminApiError | null;
}

export const LICENSE_TYPE_LABELS: Record<number, string> = {
  0: 'Standard',
  1: 'Floating',
  2: 'Demo',
  3: 'Invalid',
  4: 'Not activated',
  5: 'Expired',
  6: 'Test',
};
